import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Landmark, Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-crep-surface px-6 font-body">
      <div className="w-full max-w-md border border-crep-line bg-white p-8 sm:p-10">
        <div className="flex items-center gap-2">
          <Landmark className="h-6 w-6 text-crep-red" />
          <span className="font-heading text-sm font-extrabold tracking-tight">
            California Rental Experience Project
          </span>
        </div>
        <h1 className="mt-6 flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight text-crep-ink">
          <Lock className="h-5 w-5 text-crep-brown" /> Admin Access
        </h1>
        <p className="mt-2 text-sm text-crep-slate">
          Private dashboard for reviewing submitted experiences.
        </p>

        <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-5">
          <div>
            <Label htmlFor="email" className="font-bold text-crep-ink">Email</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 rounded-none border-crep-line py-6 focus-visible:ring-crep-red"
            />
          </div>
          <div>
            <Label htmlFor="password" className="font-bold text-crep-ink">Password</Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 rounded-none border-crep-line py-6 focus-visible:ring-crep-red"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm font-medium text-crep-red">
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full rounded-none bg-crep-red py-6 font-bold text-white hover:bg-crep-red-dark"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>

        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-6 inline-block text-sm text-crep-slate hover:text-crep-red"
        >
          ← Back to homepage
        </Link>
      </div>
    </div>
  );
}
